import type { DefaultCellTypes } from "@silevis/reactgrid";

import type { GridContract } from "@/lib/grid-contract";
import type { ProjectListItem } from "@/lib/api";

type GroupedProjects = Array<{ groupName: string; projects: ProjectListItem[] }>;

export function buildEffortProjectionsGrid(projects: ProjectListItem[]): GridContract {
  const groupedRows = groupedProjects(projects).flatMap((group) => [
    {
      rowId: `group-${group.groupName}`,
      cells: [
        { type: "header", text: group.groupName },
        { type: "text", text: "" },
        { type: "text", text: "" },
        { type: "text", text: "" },
        { type: "text", text: "" },
      ] as DefaultCellTypes[],
    },
    ...group.projects.map((project) => ({
      rowId: `project-${project.id}`,
      cells: [
        { type: "text", text: project.name },
        { type: "number", value: 160 },
        { type: "number", value: 140 },
        { type: "number", value: 120 },
        { type: "number", value: 100 },
      ] as DefaultCellTypes[],
    })),
  ]);

  return {
    title: "Effort Projections",
    columns: [
      { columnId: "project", width: 240 },
      { columnId: "currentMonth", width: 170 },
      { columnId: "nextMonth", width: 170 },
      { columnId: "month3", width: 170 },
      { columnId: "month4", width: 170 },
    ],
    rows: [
      {
        rowId: "header",
        cells: [
          { type: "header", text: "Project" },
          { type: "header", text: "Current Month" },
          { type: "header", text: "Next Month" },
          { type: "header", text: "Month +3" },
          { type: "header", text: "Month +4" },
        ],
      },
      ...groupedRows,
    ],
    stickyTopRows: 1,
    stickyLeftColumns: 1,
  };
}

export function buildPaymentPlannerGrid(projects: ProjectListItem[]): GridContract {
  const groupedRows = groupedProjects(projects).flatMap((group) => [
    {
      rowId: `group-${group.groupName}`,
      cells: [
        { type: "header", text: group.groupName },
        { type: "text", text: "" },
        { type: "text", text: "" },
        { type: "text", text: "" },
        { type: "text", text: "" },
      ] as DefaultCellTypes[],
    },
    ...group.projects.map((project) => ({
      rowId: `project-${project.id}`,
      cells: [
        { type: "text", text: project.name },
        { type: "text", text: "2026-03-15" },
        { type: "text", text: "Retainer" },
        { type: "text", text: "$12,500" },
        { type: "text", text: "Projected" },
      ] as DefaultCellTypes[],
    })),
  ]);

  return {
    title: "Payment Planner",
    columns: [
      { columnId: "project", width: 240 },
      { columnId: "date", width: 150 },
      { columnId: "type", width: 160 },
      { columnId: "amount", width: 140 },
      { columnId: "status", width: 130 },
    ],
    rows: [
      {
        rowId: "header",
        cells: [
          { type: "header", text: "Project" },
          { type: "header", text: "Date" },
          { type: "header", text: "Type" },
          { type: "header", text: "Amount" },
          { type: "header", text: "Status" },
        ],
      },
      ...groupedRows,
    ],
    stickyTopRows: 1,
    stickyLeftColumns: 1,
  };
}

export function buildCashFlowGrid(): GridContract {
  return {
    title: "Cash Flow",
    columns: [
      { columnId: "month", width: 180 },
      { columnId: "inflow", width: 170 },
      { columnId: "outflow", width: 170 },
      { columnId: "net", width: 170 },
      { columnId: "riskAdjusted", width: 190 },
    ],
    rows: [
      {
        rowId: "header",
        cells: [
          { type: "header", text: "Month" },
          { type: "header", text: "Inflow" },
          { type: "header", text: "Outflow" },
          { type: "header", text: "Net" },
          { type: "header", text: "Risk-Adjusted" },
        ],
      },
      {
        rowId: "month-apr",
        cells: [
          { type: "text", text: "Apr 2026" },
          { type: "text", text: "$180,000" },
          { type: "text", text: "$126,000" },
          { type: "text", text: "$54,000" },
          { type: "text", text: "$47,000" },
        ] as DefaultCellTypes[],
      },
    ],
    stickyTopRows: 1,
    stickyLeftColumns: 0,
  };
}

export function groupedProjects(projects: ProjectListItem[]): GroupedProjects {
  const sorted = [...projects].sort((a, b) => {
    const groupCompare = projectGroupName(a).localeCompare(projectGroupName(b), undefined, {
      sensitivity: "base",
    });
    if (groupCompare !== 0) return groupCompare;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

  const grouped = new Map<string, ProjectListItem[]>();
  sorted.forEach((project) => {
    const key = projectGroupName(project);
    grouped.set(key, [...(grouped.get(key) || []), project]);
  });

  return Array.from(grouped.entries()).map(([groupName, rows]) => ({ groupName, projects: rows }));
}

function projectGroupName(project: ProjectListItem): string {
  return project.project_group?.name || "Unassigned";
}
